// Calculs géographiques purs (sans dépendance React Native) — testés dans __tests__/geo.test.ts

import { t } from './i18n';

export type LatLng = { latitude: number; longitude: number };

const EARTH_RADIUS_M = 6371008.8;
const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Distance en mètres entre deux points (formule de haversine). */
export function distanceM(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Distances cumulées le long du tracé : cum[i] = distance du départ jusqu'au point i. */
export function cumulativeDistances(route: LatLng[]): number[] {
  const cum = new Array<number>(route.length);
  let total = 0;
  for (let i = 0; i < route.length; i++) {
    if (i > 0) total += distanceM(route[i - 1], route[i]);
    cum[i] = total;
  }
  return cum;
}

/**
 * Projection d'un point sur un segment [a, b] en coordonnées planes locales
 * (suffisant à l'échelle de quelques kilomètres). Retourne t ∈ [0, 1].
 */
function projectOnSegment(p: LatLng, a: LatLng, b: LatLng): number {
  const kx = Math.cos(toRad(p.latitude));
  const ax = a.longitude * kx,
    ay = a.latitude;
  const bx = b.longitude * kx,
    by = b.latitude;
  const px = p.longitude * kx,
    py = p.latitude;
  const dx = bx - ax,
    dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return 0;
  return Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
}

function lerp(a: LatLng, b: LatLng, t: number): LatLng {
  return {
    latitude: a.latitude + (b.latitude - a.latitude) * t,
    longitude: a.longitude + (b.longitude - a.longitude) * t,
  };
}

export type Snap = {
  /** Point le plus proche sur le tracé */
  point: LatLng;
  /** Distance en mètres entre la position et le tracé */
  distance: number;
  /** Position le long du tracé, en mètres depuis le départ */
  along: number;
  /** Index du segment (route[segment] → route[segment + 1]) */
  segment: number;
};

/**
 * Point le plus proche sur le tracé, en ne cherchant que dans la fenêtre
 * [fromAlong, toAlong] (mètres depuis le départ). La fenêtre évite de
 * « sauter » sur une autre portion quand le tracé se croise ou repasse au même endroit.
 */
export function snapToRoute(p: LatLng, route: LatLng[], cum: number[], fromAlong = 0, toAlong = Infinity): Snap | null {
  if (route.length === 0) return null;
  if (route.length === 1) {
    return { point: route[0], distance: distanceM(p, route[0]), along: 0, segment: 0 };
  }
  let best: Snap | null = null;
  for (let i = 0; i < route.length - 1; i++) {
    if (cum[i + 1] < fromAlong) continue;
    if (cum[i] > toAlong) break;
    const segLen = cum[i + 1] - cum[i];
    let t = projectOnSegment(p, route[i], route[i + 1]);
    // borne t pour rester dans la fenêtre
    if (segLen > 0) {
      const tMin = Math.max(0, (fromAlong - cum[i]) / segLen);
      const tMax = Math.min(1, (toAlong - cum[i]) / segLen);
      t = Math.max(tMin, Math.min(tMax, t));
    }
    const point = lerp(route[i], route[i + 1], t);
    const d = distanceM(p, point);
    if (!best || d < best.distance) {
      best = { point, distance: d, along: cum[i] + segLen * t, segment: i };
    }
  }
  return best;
}

/** Rayon (m, le long du tracé) dans lequel le point de retour est recalé sur le point le plus proche. */
const REJOIN_REFINE_M = 300;

/**
 * Point de retour sur le tracé, à partir de `fromAlong` (le point de sortie, jamais avant) :
 * celui qui minimise « distance depuis la position + `skipWeight` × longueur de tracé sautée ».
 * Le plus proche de la position, mais un point un peu plus éloigné est préféré s'il évite de
 * sauter une grande partie du tracé (on participe au maximum).
 * `cost` est la valeur minimisée (m).
 */
export function bestRejoinPoint(
  p: LatLng,
  route: LatLng[],
  cum: number[],
  fromAlong: number,
  skipWeight: number,
): (Snap & { cost: number }) | null {
  if (route.length < 2) {
    const s = snapToRoute(p, route, cum);
    return s ? { ...s, cost: s.distance } : null;
  }
  let best: (Snap & { cost: number }) | null = null;
  for (let i = 0; i < route.length - 1; i++) {
    if (cum[i + 1] < fromAlong) continue;
    const segLen = cum[i + 1] - cum[i];
    const tMin = segLen > 0 ? Math.max(0, (fromAlong - cum[i]) / segLen) : 0;
    const t = Math.max(tMin, projectOnSegment(p, route[i], route[i + 1]));
    const point = lerp(route[i], route[i + 1], t);
    const distance = distanceM(p, point);
    const along = cum[i] + segLen * t;
    const cost = distance + skipWeight * (along - fromAlong);
    if (!best || cost < best.cost) best = { point, distance, along, segment: i, cost };
  }
  if (!best) return null;
  // la pénalité choisit la portion du tracé ; on y prend ensuite le point le plus proche
  // (sinon il serait tiré de quelques dizaines de mètres en arrière : demi-tour inutile)
  const near = snapToRoute(
    p,
    route,
    cum,
    Math.max(fromAlong, best.along - REJOIN_REFINE_M),
    best.along + REJOIN_REFINE_M,
  );
  return near ? { ...near, cost: near.distance + skipWeight * (near.along - fromAlong) } : best;
}

/** Caps (0-360°) des portions du tracé situées à moins de `maxDistance` m de la position, dans la fenêtre. */
export function routeBearingsNear(
  p: LatLng,
  route: LatLng[],
  cum: number[],
  fromAlong: number,
  toAlong: number,
  maxDistance: number,
): number[] {
  const out: number[] = [];
  for (let i = 0; i < route.length - 1; i++) {
    if (cum[i + 1] < fromAlong) continue;
    if (cum[i] > toAlong) break;
    if (cum[i + 1] - cum[i] < 1) continue;
    const t = projectOnSegment(p, route[i], route[i + 1]);
    if (distanceM(p, lerp(route[i], route[i + 1], t)) <= maxDistance) out.push(bearing(route[i], route[i + 1]));
  }
  return out;
}

/** Écart entre deux caps (0-180°). */
export function headingDiff(a: number, b: number): number {
  const d = Math.abs((((a - b) % 360) + 360) % 360);
  return d > 180 ? 360 - d : d;
}

/** Cap du tracé (0-360°) à `along` mètres du départ. */
export function bearingAtAlong(route: LatLng[], cum: number[], along: number): number | null {
  if (route.length < 2) return null;
  const a = pointAtAlong(route, cum, Math.max(0, along - 15));
  const b = pointAtAlong(route, cum, Math.min(cum[cum.length - 1], along + 15));
  return distanceM(a, b) < 1 ? null : bearing(a, b);
}

/** Point situé à `along` mètres du départ, le long du tracé. */
export function pointAtAlong(route: LatLng[], cum: number[], along: number): LatLng {
  if (route.length === 0) throw new Error(t('Tracé vide', 'Empty track'));
  if (along <= 0) return route[0];
  const total = cum[cum.length - 1];
  if (along >= total) return route[route.length - 1];
  let lo = 0,
    hi = cum.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] <= along) lo = mid;
    else hi = mid;
  }
  const segLen = cum[hi] - cum[lo];
  return lerp(route[lo], route[hi], segLen === 0 ? 0 : (along - cum[lo]) / segLen);
}

/** Extrait la portion du tracé entre deux distances (pour colorer « fait » / « à faire »). */
export function sliceRoute(route: LatLng[], cum: number[], fromAlong: number, toAlong: number): LatLng[] {
  if (route.length < 2 || toAlong <= fromAlong) return [];
  const out: LatLng[] = [pointAtAlong(route, cum, fromAlong)];
  for (let i = 0; i < route.length; i++) {
    if (cum[i] > fromAlong && cum[i] < toAlong) out.push(route[i]);
  }
  out.push(pointAtAlong(route, cum, toAlong));
  return out;
}

/** Simplification Douglas-Peucker (tolérance en mètres) — réduit la taille des GPX importés. */
export function simplify<T extends LatLng>(route: T[], toleranceM = 5): T[] {
  if (route.length <= 2) return route.slice();
  const keep = new Uint8Array(route.length);
  keep[0] = 1;
  keep[route.length - 1] = 1;
  const stack: [number, number][] = [[0, route.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop()!;
    let maxD = 0,
      idx = -1;
    for (let i = first + 1; i < last; i++) {
      const t = projectOnSegment(route[i], route[first], route[last]);
      const d = distanceM(route[i], lerp(route[first], route[last], t));
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (idx !== -1 && maxD > toleranceM) {
      keep[idx] = 1;
      stack.push([first, idx], [idx, last]);
    }
  }
  return route.filter((_, i) => keep[i] === 1);
}

export function routeLength(route: LatLng[]): number {
  const cum = cumulativeDistances(route);
  return cum.length ? cum[cum.length - 1] : 0;
}

export function formatDistance(m: number): string {
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;
}

/** Vitesse GPS (m/s) → km/h arrondis ; null si inconnue (le GPS renvoie -1 ou null). */
export function speedKmh(metersPerSecond: number | null | undefined): number | null {
  if (metersPerSecond == null || !Number.isFinite(metersPerSecond) || metersPerSecond < 0) return null;
  const kmh = metersPerSecond * 3.6;
  return kmh < 3 ? 0 : Math.round(kmh); // à l'arrêt, le GPS « tremble » : on affiche 0
}

/** Cap (0-360°, 0 = nord) pour aller de a vers b. */
export function bearing(a: LatLng, b: LatLng): number {
  const φ1 = toRad(a.latitude),
    φ2 = toRad(b.latitude);
  const Δλ = toRad(b.longitude - a.longitude);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Différence de cap signée (-180..180) : positif = vers la droite. */
export function turnAngle(from: number, to: number): number {
  return ((to - from + 540) % 360) - 180;
}

/** Plage de zoom utilisée en navigation (16 ≈ quelques rues autour de la voiture). */
export const NAV_ZOOM = { min: 12, max: 19, default: 16 };

/** Apple Plans travaille en altitude (m) et Google Maps en niveau de zoom : équivalence approchée. */
export function altitudeForZoom(zoom: number): number {
  return Math.round(1000 * 2 ** (16 - zoom));
}
